import { Box, TableCell, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { DESC } from 'framework/list/baseListHelper';
import ColumnActionsMenu from './ColumnActionsMenu';
import ListTableActionButton from './ListTableActionButton';
import OverflowTooltip from './OverflowTooltip';
import { actionCellSx, headerLabelSx, tableHeadSx } from './styles';
import type { ListTableColumn, ListTableProps, ListTableRow } from './types';
import { getNextSort } from './utils';

interface ListTableHeaderProps<Row extends ListTableRow> {
  columns: ListTableColumn<Row>[];
  columnVisibilityControl: ListTableProps<Row>['columnVisibilityControl'];
  defaultColumnWidth: number;
  hasActions: boolean;
  indentFirstColumn: boolean;
  mainAction: ListTableProps<Row>['mainAction'];
  sort: ListTableProps<Row>['sort'];
  onSortChange: ListTableProps<Row>['onSortChange'];
}

const ListTableHeader = <Row extends ListTableRow>({
  columns,
  columnVisibilityControl,
  defaultColumnWidth,
  hasActions,
  indentFirstColumn,
  mainAction,
  sort,
  onSortChange,
}: ListTableHeaderProps<Row>) => {
  const headerColumns: Omit<ListTableColumn<Row>, 'render'>[] = columnVisibilityControl
    ? columnVisibilityControl.columns.map(option => columns.find(column => column.id === option.id) ?? option)
    : columns;

  return (
    <TableHead sx={tableHeadSx}>
      <TableRow>
        {headerColumns.map(column => {
          const visibleIndex = columns.findIndex(item => item.id === column.id);
          const isSorted = sort?.columnId === column.id;
          const width = column.width ?? defaultColumnWidth;
          const label = (
            <OverflowTooltip>
              <Typography component="span" color="brand" fontSize={14} fontWeight={600} noWrap sx={headerLabelSx}>
                {column.label}
              </Typography>
            </OverflowTooltip>
          );

          return (
            <TableCell
              key={column.id}
              align={column.align}
              sx={{
                width,
                display: visibleIndex === -1 ? 'none' : undefined,
                '&::before': {
                  content: visibleIndex > 0 ? '""' : 'none',
                  position: 'absolute',
                  left: 0,
                  top: '25%',
                  height: '50%',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  pointerEvents: 'none',
                },
                ...(indentFirstColumn && visibleIndex === 0 ? { pl: 4 } : {}),
              }}
            >
              <Box
                sx={{
                  width,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  pl: visibleIndex > 0 ? 1.5 : 0,
                  boxSizing: 'border-box',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1, textAlign: column.align }}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={isSorted}
                      direction={isSorted && sort?.direction === DESC ? 'desc' : 'asc'}
                      onClick={() => onSortChange?.(getNextSort(column.id, sort ?? null))}
                    >
                      {label}
                    </TableSortLabel>
                  ) : (
                    label
                  )}
                </Box>
                {columnVisibilityControl && (
                  <ColumnActionsMenu column={column.id} visibility={columnVisibilityControl} />
                )}
              </Box>
            </TableCell>
          );
        })}
        {hasActions && (
          <TableCell aria-label="Actions" align="right" sx={actionCellSx}>
            {mainAction && (
              <ListTableActionButton
                action={mainAction}
                disabled={mainAction.isDisabled}
                onClick={mainAction.onClick}
              />
            )}
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );
};

export default ListTableHeader;
